import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { messageAPI, chatFileAPI } from '../../lib/api';
import { cn, getInitials, getUploadErrorMessage } from '../../lib/utils';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
    X,
    Send,
    Smile,
    Paperclip,
    Image,
    MoreHorizontal,
    Edit2,
    Trash2,
    Loader2,
    ArrowLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

const ThreadView = ({ parentMessage, channelId, onClose, ws }) => {
    const { user } = useAuth();
    const { refreshStorage } = useWorkspace();
    const [replies, setReplies] = useState([]);
    const [newReply, setNewReply] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [pendingImages, setPendingImages] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const repliesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        loadReplies();
    }, [parentMessage.id]);

    useEffect(() => {
        scrollToBottom();
    }, [replies]);

    const loadReplies = async () => {
        try {
            const res = await messageAPI.getReplies(parentMessage.id);
            setReplies(res.data.messages || []);
        } catch (error) {
            console.error('Failed to load replies:', error);
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const sendReply = async () => {
        if (!newReply.trim() && pendingImages.length === 0) return;

        setSending(true);
        try {
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                    type: 'message',
                    content: newReply,
                    parent_id: parentMessage.id,
                    mentions: [],
                    attachments: pendingImages,
                }));
                setNewReply('');
                setPendingImages([]);
                setTimeout(() => loadReplies(), 500);
            } else {
                await messageAPI.create(channelId, {
                    content: newReply,
                    channel_id: channelId,
                    parent_id: parentMessage.id,
                    mentions: [],
                    attachments: pendingImages,
                });
                setNewReply('');
                setPendingImages([]);
                loadReplies();
            }
        } catch (error) {
            toast.error('Gagal mengirim balasan');
        } finally {
            setSending(false);
        }
    };

    const uploadImageFile = async (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setUploadingImage(true);
        try {
            const response = await chatFileAPI.upload(file, channelId);
            setPendingImages(prev => [...prev, response.data]);
            toast.success('Gambar ditambahkan');
            refreshStorage();
        } catch (error) {
            toast.error(getUploadErrorMessage(error));
        } finally {
            setUploadingImage(false);
        }
    };

    const handleImagePaste = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        if (!imageItem) return;
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) await uploadImageFile(file);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleImageDrop = async (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        for (const file of files) await uploadImageFile(file);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendReply();
        }
    };

    const addEmoji = (emoji) => {
        setNewReply(prev => prev + emoji.native);
        setShowEmojiPicker(false);
        inputRef.current?.focus();
    };

    const handleDeleteReply = async (replyId) => {
        try {
            await messageAPI.delete(replyId);
            setReplies(prev => prev.filter(r => r.id !== replyId));
            toast.success('Balasan dihapus');
        } catch (error) {
            toast.error('Gagal menghapus balasan');
        }
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' };
        const timeStr = date.toLocaleTimeString('id-ID', timeOpts) + ' WIB';
        const dateInWIB = date.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        const nowInWIB = now.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        if (dateInWIB === nowInWIB) return timeStr;
        const dateDisplay = date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' });
        return `${dateDisplay}, ${timeStr}`;
    };

    return (
        <div className="w-96 border-l border-gray-200 flex flex-col bg-white">
            {/* Header */}
            <div className="h-14 border-b border-gray-200 px-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="font-semibold text-gray-800">Thread</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                    <X className="w-4 h-4" />
                </Button>
            </div>

            {/* Parent Message */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        {getInitials(parentMessage.user?.name || 'U')}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-baseline gap-2 mb-0.5">
                            <span className="font-semibold text-gray-900 text-sm">
                                {parentMessage.user?.name || 'Unknown'}
                            </span>
                            <span className="text-xs text-gray-400">
                                {formatTime(parentMessage.created_at)}
                            </span>
                        </div>
                        <p className="text-gray-800 text-sm whitespace-pre-wrap">{parentMessage.content}</p>
                        <p className="text-xs text-gray-500 mt-2">
                            {replies.length} balasan
                        </p>
                    </div>
                </div>
            </div>

            {/* Replies */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                    <div className="flex justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <>
                        {replies.map((reply) => (
                            <ReplyItem
                                key={reply.id}
                                reply={reply}
                                currentUser={user}
                                onDelete={handleDeleteReply}
                            />
                        ))}
                        <div ref={repliesEndRef} />
                    </>
                )}
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t border-gray-200">
                {pendingImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                        {pendingImages.map((img, idx) => (
                            <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs">
                                <Image className="w-3 h-3 text-gray-500" />
                                <span className="truncate max-w-[100px]">{img.filename}</span>
                                <button onClick={() => setPendingImages(prev => prev.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <div className="flex-1 relative">
                        <Textarea
                            ref={inputRef}
                            value={newReply}
                            onChange={(e) => setNewReply(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onPaste={handleImagePaste}
                            onDragOver={handleImageDragOver}
                            onDrop={handleImageDrop}
                            placeholder="Balas thread... (paste/drop gambar)"
                            className="min-h-[44px] max-h-24 resize-none pr-12 text-sm"
                            rows={1}
                        />
                        <div className="absolute right-2 bottom-2">
                            <div className="relative">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                >
                                    <Smile className="w-4 h-4" />
                                </Button>
                                {showEmojiPicker && (
                                    <div className="absolute bottom-10 right-0 z-50">
                                        <Picker
                                            data={data}
                                            onEmojiSelect={addEmoji}
                                            theme="light"
                                            previewPosition="none"
                                            skinTonePosition="none"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Button
                        onClick={sendReply}
                        disabled={!newReply.trim() || sending}
                        size="sm"
                        className="h-10"
                    >
                        {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Reply Item Component
const ReplyItem = ({ reply, currentUser, onDelete }) => {
    const isOwn = reply.user_id === currentUser?.id;

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' };
        const timeStr = date.toLocaleTimeString('id-ID', timeOpts) + ' WIB';
        const dateInWIB = date.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        const nowInWIB = now.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        if (dateInWIB === nowInWIB) return timeStr;
        const dateDisplay = date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' });
        return `${dateDisplay}, ${timeStr}`;
    };

    return (
        <div className="group flex gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                {getInitials(reply.user?.name || 'U')}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="font-semibold text-gray-900 text-sm">
                        {reply.user?.name || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-400">
                        {formatTime(reply.created_at)}
                    </span>
                    {reply.is_edited && (
                        <span className="text-xs text-gray-400">(diedit)</span>
                    )}
                </div>
                <p className="text-gray-800 text-sm whitespace-pre-wrap">{reply.content}</p>
            </div>

            {/* Actions */}
            {isOwn && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => onDelete(reply.id)}
                                className="text-red-600"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Hapus
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}
        </div>
    );
};

export default ThreadView;
